import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Sparkles, X, Crown, Loader2, Zap, Eye, Heart, Star, Send } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';

const BENEFITS = [
  { icon: Heart, title: 'Unlimited likes', desc: 'Swipe as much as you want, no daily cap' },
  { icon: Eye, title: 'See who likes you', desc: 'Skip the guesswork — instantly view your admirers' },
  { icon: Zap, title: 'Priority in Discover', desc: 'Your profile shows up earlier to matches' },
  { icon: Send, title: 'Unlimited messages', desc: 'No free-tier message limit' },
  { icon: Star, title: 'Premium badge', desc: 'Gold crown next to your name signals verified profile' },
  { icon: Sparkles, title: 'Submit unlimited listings', desc: 'Post as many businesses / events / communities as you want' },
];

function PlanCard({ plan, isActive, onSelect, isYearly }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={`premium-plan-${plan.id}`}
      className={`relative text-left p-6 rounded-2xl border-2 transition-all ${
        isActive
          ? 'border-[#E9C46A] bg-gradient-to-br from-[#E9C46A]/20 to-[#E07A5F]/10 shadow-xl shadow-[#E9C46A]/20'
          : 'border-[#F4F1DE] bg-white hover:border-[#E07A5F]'
      }`}
    >
      {isYearly && (
        <span className="absolute -top-3 left-6 px-3 py-1 bg-[#C8553D] text-white text-[10px] font-bold rounded-full uppercase tracking-wider">
          Save 37%
        </span>
      )}
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-lg font-bold text-[#2B2D42]">{plan.label}</h3>
        {isActive && <div className="w-5 h-5 rounded-full bg-[#E9C46A] flex items-center justify-center"><Check className="w-3 h-3 text-white" strokeWidth={3} /></div>}
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-3xl font-serif font-bold text-[#C8553D]" style={{ fontFamily: 'Playfair Display, serif' }}>
          ${plan.amount}
        </span>
        <span className="text-sm text-[#6D6875]">/ {plan.interval}</span>
      </div>
      {plan.savings && (
        <p className="text-xs text-[#2A9D8F] font-semibold mt-1">Save CA${plan.savings} annually</p>
      )}
    </button>
  );
}

export default function Premium() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [checkingOut, setCheckingOut] = useState(false);
  const [polling, setPolling] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const pollAttemptsRef = useRef(0);

  useEffect(() => {
    (async () => {
      try {
        const res = await api('/subscriptions/plans');
        setPlans(res.plans);
      } catch {}
    })();
  }, []);

  // Detect ?session_id= on return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId || polling) return;
    setPolling(true);
    setStatusMessage('Verifying your payment...');
    pollStatus(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  async function pollStatus(sessionId) {
    pollAttemptsRef.current += 1;
    if (pollAttemptsRef.current > 10) {
      setStatusMessage('');
      setPolling(false);
      toast.error('Payment verification timed out. Please refresh.');
      return;
    }
    try {
      const data = await api(`/subscriptions/status/${sessionId}`);
      if (data.payment_status === 'paid') {
        setStatusMessage('');
        setPolling(false);
        toast.success('Welcome to Premium! 👑');
        await refreshUser();
        setSearchParams({});
        return;
      }
      if (data.status === 'expired') {
        setStatusMessage('');
        setPolling(false);
        toast.error('Payment session expired.');
        setSearchParams({});
        return;
      }
      setTimeout(() => pollStatus(sessionId), 2000);
    } catch (err) {
      setStatusMessage('');
      setPolling(false);
      toast.error(err.message || 'Failed to verify');
    }
  }

  async function handleUpgrade() {
    setCheckingOut(true);
    try {
      const { url } = await api('/subscriptions/checkout', {
        method: 'POST',
        body: JSON.stringify({
          plan: selectedPlan,
          origin_url: window.location.origin,
        }),
      });
      window.location.href = url;
    } catch (err) {
      toast.error(err.message || 'Checkout failed');
      setCheckingOut(false);
    }
  }

  const [portalBusy, setPortalBusy] = useState(false);
  async function openPortal() {
    setPortalBusy(true);
    try {
      const { url } = await api('/subscriptions/portal', {
        method: 'POST',
        body: JSON.stringify({ return_url: window.location.origin + '/app/premium' }),
      });
      window.location.href = url;
    } catch (err) {
      toast.error(err.message || 'Could not open billing portal');
      setPortalBusy(false);
    }
  }

  if (user?.is_premium) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10" data-testid="premium-page">
        <div className="bg-gradient-to-br from-[#E9C46A]/30 to-[#E07A5F]/20 border-2 border-[#E9C46A] rounded-2xl p-8 text-center" data-testid="premium-active-card">
          <Crown className="w-16 h-16 text-[#E9C46A]" fill="#E9C46A" strokeWidth={1.5} style={{ margin: '0 auto' }} />
          <h1 className="text-3xl font-serif font-bold text-[#2B2D42] mt-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            You're Premium
          </h1>
          <p className="text-[#6D6875] mt-2">
            Thank you for supporting AfroConnect. Enjoy unlimited likes, priority discovery, and all Premium perks.
          </p>
          {user.premium_expires && (
            <p className="text-xs text-[#B5B5B5] mt-4" data-testid="premium-expires">
              Renews / expires: {new Date(user.premium_expires).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          )}
          <button
            onClick={() => navigate('/app/discover')}
            data-testid="premium-active-continue"
            className="mt-6 inline-flex items-center gap-2 bg-[#C8553D] hover:bg-[#9E3A26] text-white font-bold px-6 py-3 rounded-full"
          >
            Continue to Discover
          </button>
          <button
            onClick={openPortal}
            disabled={portalBusy}
            data-testid="premium-manage-button"
            className="mt-3 block mx-auto text-sm font-bold text-[#6D6875] hover:text-[#C8553D] underline disabled:opacity-60"
          >
            {portalBusy ? 'Opening…' : 'Manage subscription (update card, cancel, view invoices)'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="premium-page">
      {/* Polling overlay */}
      <AnimatePresence>
        {polling && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center"
            data-testid="premium-polling-overlay"
          >
            <div className="bg-white rounded-2xl p-8 max-w-sm text-center">
              <Loader2 className="w-10 h-10 animate-spin text-[#C8553D] mx-auto" />
              <h3 className="mt-4 font-serif font-bold text-lg text-[#2B2D42]" style={{ fontFamily: 'Playfair Display, serif' }}>Confirming payment</h3>
              <p className="text-sm text-[#6D6875] mt-1">{statusMessage}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-[#E9C46A]/30 to-[#E07A5F]/20 rounded-full mb-4">
          <Crown className="w-4 h-4 text-[#E9C46A]" fill="#E9C46A" />
          <span className="text-xs font-bold uppercase tracking-wider text-[#C8553D]">AfroConnect Premium</span>
        </div>
        <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#2B2D42]" style={{ fontFamily: 'Playfair Display, serif' }}>
          Connect deeper.<br />Find sooner.
        </h1>
        <p className="text-[#6D6875] mt-4 max-w-lg mx-auto">
          Premium unlocks unlimited likes, priority discovery, the ability to see who likes you, and a verified profile badge.
        </p>
      </div>

      {/* Plans */}
      {plans && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <PlanCard
            plan={{ id: 'monthly', ...plans.monthly }}
            isActive={selectedPlan === 'monthly'}
            onSelect={() => setSelectedPlan('monthly')}
          />
          <PlanCard
            plan={{ id: 'yearly', ...plans.yearly }}
            isActive={selectedPlan === 'yearly'}
            onSelect={() => setSelectedPlan('yearly')}
            isYearly
          />
        </div>
      )}

      {/* Upgrade button */}
      <button
        onClick={handleUpgrade}
        disabled={checkingOut || !plans}
        data-testid="premium-upgrade-button"
        className="w-full bg-gradient-to-r from-[#E07A5F] to-[#C8553D] hover:from-[#C8553D] hover:to-[#9E3A26] disabled:opacity-60 text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 shadow-xl shadow-[#C8553D]/40 text-base mb-8 transition-all hover:scale-[1.01]"
      >
        {checkingOut ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Crown className="w-5 h-5" fill="currentColor" /> Upgrade to Premium</>}
      </button>

      {/* Benefits grid */}
      <div className="grid sm:grid-cols-2 gap-3" data-testid="premium-benefits-grid">
        {BENEFITS.map(({ icon: Icon, title, desc }) => (
          <div key={title} className="flex items-start gap-3 p-4 bg-white border border-[#F4F1DE] rounded-2xl">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#E9C46A]/30 to-[#E07A5F]/20 flex items-center justify-center flex-shrink-0">
              <Icon className="w-4.5 h-4.5 text-[#C8553D]" />
            </div>
            <div>
              <h4 className="font-bold text-sm text-[#2B2D42]">{title}</h4>
              <p className="text-xs text-[#6D6875] leading-relaxed">{desc}</p>
            </div>
          </div>
        ))}
      </div>

      <p className="text-center text-xs text-[#B5B5B5] mt-8">
        Secure checkout by Stripe. Cancel anytime from your profile.
      </p>
    </div>
  );
}
